import { Link } from 'react-router-dom';
import { startScan, type ScanView } from '../../api/catalog';
import { ca } from '../../i18n/ca';
import { fmtWhen } from './shared';

interface Props {
  scans: ScanView[];
  onScans: (update: (list: ScanView[]) => ScanView[]) => void;
}

const SCAN_CHIP: Record<ScanView['status'], string> = {
  STARTING: 'warn',
  RUNNING: 'warn',
  DONE: 'ok',
  FAILED: 'bad',
};

/** Catalog tab: each server's newest scan as a table, and the door to the catalogue itself. */
export function CatalogTab({ scans, onScans }: Props) {
  return (
    <section className="stack" aria-label={ca.catalogScans}>
      <p className="muted small">{ca.adminUi.intros.catalog}</p>
      {scans.length === 0 ? (
        <div className="empty">
          <i className="bi bi-grid" aria-hidden="true" />
          <p>{ca.noScans}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data scans">
            <thead>
              <tr>
                <th>{ca.adminUi.colPreset}</th>
                <th>{ca.adminUi.colState}</th>
                <th>{ca.adminUi.colStarted}</th>
                <th>{ca.adminUi.colError}</th>
                <th>{ca.adminUi.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.serverName ?? s.modSetHash.slice(0, 8)}</strong>
                    <br />
                    <span className="muted small">
                      {ca.adminUi.chunks(s.chunksReceived, s.chunksExpected)}
                    </span>
                  </td>
                  <td>
                    <span className={`chip ${SCAN_CHIP[s.status] ?? ''}`}>
                      {ca.scanStates[s.status] ?? s.status}
                    </span>
                  </td>
                  <td className="muted small nowrap">{fmtWhen(s.startedAt)}</td>
                  <td className="small">
                    {s.error ? <span className="error">{s.error}</span> : '—'}
                  </td>
                  <td>
                    {s.serverId !== null && s.status !== 'RUNNING' && s.status !== 'STARTING' && (
                      <button
                        type="button"
                        className="small"
                        onClick={() =>
                          void startScan(s.serverId as number, true)
                            .then((started) =>
                              onScans((list) => [
                                started,
                                ...list.filter(
                                  (x) => x.id !== started.id && x.serverId !== started.serverId,
                                ),
                              ]),
                            )
                            .catch(() => undefined)
                        }
                      >
                        <i className="bi bi-arrow-repeat" aria-hidden="true" /> {ca.rescan}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="row actions">
        <Link className="button" to="/catalog">
          <i className="bi bi-grid" aria-hidden="true" /> {ca.openCatalog}
        </Link>
      </div>
    </section>
  );
}
